import { AxiosError } from "axios";
import React, { useCallback, useMemo, useRef, useState } from "react";
import LoadingPage from "../LoadingPage";
import { ColumnDefs } from "./SearchPageDetails/SearchColumnDefs";
import ColumnCustomizeFilter from "../../components/ColumnCustomizeFilter";
import APIErrorHandling from "../error/ApiErrorHandlingPage";
import QueryParams from "../../interfaces/QueryParams";
import SearchBar from "./SearchPageDetails/SearchBar";
import DefaultGrid from "../../components/DefaultGrid/DefaultGrid";
import SearchGridServerSideDatasource from "../../api/GridDataSources/SearchGridServerSideDataSource";
import ExportButton from "./SearchPageDetails/ExportButton";
import { AgGridReact } from "ag-grid-react";
import SearchGridVendorInfo from "../../interfaces/SearchGridVendorInfo";
import { LOADING_TEXT } from "@/constants";
import { PaginationChangedEvent } from "ag-grid-community";
import ExportResults from "@/pages/SearchPage/SearchPageDetails/ExportResults";
import { useSearchOnEnter } from "@/hooks/useSearchOnEnter";

const SearchPage: React.FC = () => {
  const [isSearchEnabled, setIsSearchEnabled] = useState(false);  // State to track if search is enabled
  const [doSearchFn, setDoSearchFn] = useState<(() => void) | null>(null);

  const [searchParams, setSearchParams] = useState({
    commodity: [],
    country: [],
    itemDescription: "",
    itemNumber: undefined,
    vendorName: "",
    vendorNumber: undefined,
  } as QueryParams);

  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errorStatus, setErrorStatus] = useState<
    AxiosError | { message?: string } | string
  >("");
  const [errorType, setErrorType] = useState("");
  const [rowCount, setRowCount] = useState<number | null>(null);
  const [showGrid, setShowGrid] = useState(false);

  const gridRef = useRef<AgGridReact<SearchGridVendorInfo> | null>(null);

  // Add new trigger state to force the datasource to refresh
  const [searchTrigger, setSearchTrigger] = useState(0);

  // Adding new callback to update the row count for the export results button
  const exportResultRowCount = useCallback((count: number) => {
    setRowCount(count);
  }, []);

  // Adding exportResultRowCount as dependency
  const searchGridServerSideDatasource = useMemo(() => {
    return new SearchGridServerSideDatasource(
      searchParams,
      gridRef,
      exportResultRowCount,
    );
  }, [searchParams, searchTrigger, exportResultRowCount]);
  // Handle pagination change to refresh grid layout - TODO: Optimize this further if needed
  const handlePaginationPageSizeChanged = useCallback(
    (event: PaginationChangedEvent) => {
      const api = event.api;
      setTimeout(() => {
        if (api) {
          api.refreshCells();
          api.redrawRows();
        }
      }, 0);
    },
    [],
  );

  const handleSearchClick = useCallback(() => {
    if (!showGrid) {
      setShowGrid(true);
    }
    setSearchTrigger((prev) => prev + 1); // Increment trigger to force datasource update
  }, [showGrid]);

  if (loading) {
    return <LoadingPage title={LOADING_TEXT} />;
  }
  if (errorStatus) {
    return <APIErrorHandling error={errorStatus} type={errorType} />;
  }
  
  const handleDoSearchChange = useCallback((fn: () => void) => {
    setDoSearchFn(() => fn);
  }, []);
  
  const renderAgGridTableWithCustomizeColumnView = () => {
    return (
      <div className="w-full">
        {showGrid && (
          <div>
            <div
              id="grid-control"
              className="w-full flex flex-row justify-start items-center pl-4 pt-4 pb-1"
            >
              <div id="column-customize-view-button" className="flex-item pr-8">
                <button
                  type="button"
                  className="flex flex-row place-content-end"
                  onClick={() => setIsOpen(!isOpen)}
                >
                  <div className="self-center justify-items-start bg-blue w-6 h-6 rounded-full">
                    <div className="icon-filter_list text-xl text-white inline-block mt-1" />
                  </div>
                  <p className="inline-block text-blue px-4">
                    Customize Column View
                  </p>
                </button>
              </div>

              {rowCount !== 0 && (
                <div id="export-results-button" className="flex-item pr-8">
                  <ExportResults
                    setErrorType={setErrorType}
                    setErrorStatus={setErrorStatus}
                    setLoading={setLoading}
                    searchParams={searchParams}
                  />
                </div>
              )}
              <div id="export-button" className="flex-item pr-8">
                <ExportButton
                  setErrorType={setErrorType}
                  setErrorStatus={setErrorStatus}
                  setLoading={setLoading}
                />
              </div>
            </div>
            <div className="pl-4 items-center justify-start">
              {isOpen && gridRef.current && (
                <ColumnCustomizeFilter
                  gridApi={gridRef.current.api}
                  onClickOutside={() => setIsOpen(false)}
                />
              )}
            </div>

            <div className="w-full shadow-md">
              <DefaultGrid
                columnDefs={ColumnDefs}
                datasource={searchGridServerSideDatasource}
                loading={loading}
                onRowClicked={() => {}}
                onPaginationPageSizeChanged={handlePaginationPageSizeChanged}
                rowId={(params) => params.data._id ?? ""}
                noRowsFoundText={`No Record(s) Found`}
                quickFilterText={searchParams.itemDescription}
                ref={gridRef}
              />
            </div>
            <div className="h-10" />
          </div>
        )}
      </div>
    );
  };

  useSearchOnEnter({
    isEnabled: isSearchEnabled,
    onSearch: () => doSearchFn?.(),
    searchParams: searchParams,  // Pass current searchParams
  });

  return (
    <div className="w-full h-full flex flex-col">
      <div className="pb-2 mt-20 w-full flex justify-center 2xl:pt-6">
        <SearchBar
          searchParams={searchParams}
          updateSearchParams={setSearchParams}
          handleSearchClick={handleSearchClick}
          searchCardExpanded={setIsSearchEnabled}
          onDoSearchChange={handleDoSearchChange}
        />
      </div>
      <div className="w-full px-5">
        {renderAgGridTableWithCustomizeColumnView()}
      </div>
      <div id="error-handling" className="w-full px-8">
        {!loading && errorStatus && (
          <APIErrorHandling error={errorStatus} type={errorType} />
        )}
      </div>
    </div>
  );
};

export default SearchPage;
